import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Briefcase,
  Building2,
  CheckCircle2,
  Eye,
  Globe2,
  Loader2,
  Mail,
  MapPin,
  PauseCircle,
  PlayCircle,
  Search,
  ShieldCheck,
  UsersRound,
  X
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { adminAPI } from '../../services/api';

const inputClass = 'h-10 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-700 outline-none transition focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
const textareaClass = 'min-h-[120px] w-full resize-y rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700 outline-none transition focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

const accountOptions = [
  { value: 'all', label: 'All accounts' },
  { value: 'active', label: 'Active' },
  { value: 'pending_verification', label: 'Awaiting KYC approval' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'inactive', label: 'Inactive' }
];

const verificationOptions = [
  { value: 'all', label: 'All KYC' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In review' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'requires_resubmission', label: 'Needs resubmission' }
];

const sortOptions = [
  { value: 'created_at', label: 'Newest' },
  { value: 'company_name', label: 'Company' },
  { value: 'account_status', label: 'Account status' },
  { value: 'verification_status', label: 'KYC status' },
  { value: 'active_jobs', label: 'Active jobs' },
  { value: 'matches', label: 'Matches' }
];

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const accountStatusLabel = (status) => ({
  active: 'Active',
  pending_verification: 'Awaiting KYC approval',
  suspended: 'Suspended',
  inactive: 'Inactive'
}[String(status || '').toLowerCase()] || labelize(status));

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDate = (value, fallback = 'Not recorded') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const formatDateTime = (value, fallback = 'Not recorded') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const extractError = (error, fallback) => (
  error?.response?.data?.error ||
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['active', 'verified'].includes(normalized)) return 'border-emerald-100/50 bg-emerald-50 text-emerald-600';
  if (['pending', 'pending_verification', 'in_review', 'requires_resubmission'].includes(normalized)) return 'border-amber-100/50 bg-amber-50 text-amber-600';
  if (['suspended', 'rejected', 'inactive'].includes(normalized)) return 'border-rose-100/50 bg-rose-50 text-rose-600';
  return 'border-slate-200/50 bg-slate-50 text-slate-600';
};

const initialsFor = (name) => (
  String(name || 'Employer')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
);

const metadataValue = (metadata, keys = []) => {
  for (const key of keys) {
    const value = key.split('.').reduce((current, part) => current?.[part], metadata);
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
};

const StatCard = ({ icon: Icon, label, value, detail }) => (
  <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-6">
      <h3 className="text-sm font-semibold text-slate-600">{label}</h3>
      <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-blue-600 border border-slate-100/50">
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div>
      <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
      {detail && <p className="text-xs font-medium text-slate-400 mt-2">{detail}</p>}
    </div>
  </div>
);

const Badge = ({ status, label }) => (
  <span className={`inline-flex rounded-md border px-2.5 py-1 text-[11px] font-semibold ${statusTone(status)}`}>
    {label || labelize(status)}
  </span>
);

const EmptyState = ({ onReset }) => (
  <div className="flex min-h-[400px] flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50/50 px-6 text-center mx-6 my-6">
    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm text-slate-400">
      <Building2 className="h-8 w-8" />
    </div>
    <h2 className="text-lg font-bold text-slate-900">No employers found</h2>
    <p className="mt-2 max-w-md text-sm text-slate-500">
      Adjust the search or filters to view employer accounts registered on the platform.
    </p>
    <button
      type="button"
      onClick={onReset}
      className="mt-6 inline-flex h-11 items-center justify-center rounded-xl bg-slate-900 px-6 text-sm font-semibold text-white transition hover:bg-blue-600"
    >
      Reset filters
    </button>
  </div>
);

const EmployersSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] p-4 sm:p-6 lg:p-8 font-sans">
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="space-y-3">
        <SkeletonBlock className="h-8 w-48 rounded-lg" />
        <SkeletonBlock className="h-4 w-96 rounded-md" />
      </div>
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <SkeletonBlock key={item} className="h-36 rounded-[24px]" />)}
      </div>
      <SkeletonBlock className="h-[600px] rounded-[24px]" />
    </div>
  </div>
);

function DetailItem({ label, value }) {
  return (
    <div className="flex flex-col gap-1 p-4 rounded-[16px] bg-slate-50/50 border border-slate-100">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-900 break-words">{value || 'Not set'}</p>
    </div>
  );
}

function EmployerDetailsModal({ employer, onClose, onAction }) {
  if (!employer) return null;
  const country = metadataValue(employer.companyMetadata, ['country.name', 'country.label', 'countryCode']);
  const socialLinks = employer.companySocialLinks || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <section className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-6 sm:p-8">
          <div className="flex items-center gap-5 min-w-0">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-blue-50 border border-blue-100 text-xl font-bold text-blue-600 shadow-sm">
              {employer.companyLogoUrl ? <img src={employer.companyLogoUrl} alt="" className="h-full w-full object-cover" /> : initialsFor(employer.companyName)}
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Employer Profile</p>
              <h2 className="truncate text-3xl font-bold tracking-tight text-slate-900">{employer.companyName}</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition hover:bg-slate-100 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 sm:p-8 custom-scrollbar">
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <DetailItem label="Account access" value={<Badge status={employer.accountStatus} label={accountStatusLabel(employer.accountStatus)} />} />
            <DetailItem label="KYC Status" value={<Badge status={employer.verificationStatus} />} />
            <DetailItem label="Jobs Overview" value={`${formatNumber(employer.stats?.activeJobs)} active / ${formatNumber(employer.stats?.totalJobs)} total`} />
            <DetailItem label="Total Matches" value={formatNumber(employer.stats?.totalMatches)} />
          </div>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-8">
              <section className="rounded-[24px] border border-slate-100 bg-white overflow-hidden shadow-sm">
                <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                  <h3 className="font-semibold text-slate-900">Company Overview</h3>
                </div>
                <div className="grid gap-4 p-6 sm:grid-cols-2">
                  <DetailItem label="Industry" value={employer.industry} />
                  <DetailItem label="Sector" value={employer.sector} />
                  <DetailItem label="Company Type" value={labelize(employer.companyType)} />
                  <DetailItem label="Account Type" value={labelize(employer.accountType)} />
                  <DetailItem label="Size" value={employer.companySize || employer.employeeCountRange} />
                  <DetailItem label="Founded" value={employer.foundedYear} />
                  <DetailItem label="Country" value={country} />
                  <DetailItem label="Headquarters" value={employer.headquartersLocation} />
                  <DetailItem label="Website" value={employer.websiteUrl} />
                  <DetailItem label="Subscription" value={labelize(employer.subscriptionPlan)} />
                </div>
              </section>

              <section className="rounded-[24px] border border-slate-100 bg-white overflow-hidden shadow-sm">
                <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                  <h3 className="font-semibold text-slate-900">Description & Context</h3>
                </div>
                <div className="space-y-4 p-6">
                  <DetailItem label="Description" value={employer.companyDescription} />
                  <DetailItem label="Goals and Vision" value={employer.goalsAndVision} />
                  <DetailItem label="Culture" value={Object.keys(employer.companyCulture || {}).length ? JSON.stringify(employer.companyCulture, null, 2) : null} />
                  <DetailItem label="Social Links" value={Object.keys(socialLinks).length ? JSON.stringify(socialLinks, null, 2) : null} />
                </div>
              </section>
            </div>

            <aside className="space-y-6">
              <section className="rounded-[24px] border border-slate-100 bg-white overflow-hidden shadow-sm">
                <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                  <h3 className="font-semibold text-slate-900">Primary Contact</h3>
                </div>
                <div className="p-6 space-y-4">
                  <DetailItem label="Name" value={employer.contact?.name} />
                  <DetailItem label="Email" value={employer.contact?.email} />
                  <DetailItem label="Phone" value={employer.contact?.phone} />
                  <DetailItem label="Job Title" value={employer.jobTitle} />
                </div>
              </section>

              <section className="rounded-[24px] border border-slate-100 bg-white overflow-hidden shadow-sm">
                <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                  <h3 className="font-semibold text-slate-900">Activity Log</h3>
                </div>
                <div className="p-6 space-y-4">
                  <DetailItem label="Created At" value={formatDateTime(employer.createdAt)} />
                  <DetailItem label="Last Updated" value={formatDateTime(employer.updatedAt)} />
                  <DetailItem label="Last Login" value={formatDateTime(employer.contact?.lastLoginAt)} />
                  <DetailItem label="Last Activity" value={formatDateTime(employer.latest?.activityAt)} />
                </div>
              </section>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => onAction(employer, 'suspended')}
                  disabled={employer.accountStatus === 'suspended'}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-rose-50 text-sm font-semibold text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PauseCircle className="h-4 w-4" />
                  Suspend Account
                </button>
                <button
                  type="button"
                  onClick={() => onAction(employer, 'active')}
                  disabled={employer.accountStatus !== 'suspended'}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-50 text-sm font-semibold text-emerald-600 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PlayCircle className="h-4 w-4" />
                  Reactivate Account
                </button>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

function AccountActionModal({ action, onClose, onSubmit, saving }) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    setReason('');
  }, [action?.employer?.id, action?.status]);

  if (!action) return null;
  const suspending = action.status === 'suspended';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <section className="w-full max-w-lg overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="border-b border-slate-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            {suspending ? 'Suspend Account' : 'Reactivate Account'}
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            {suspending
              ? 'This revokes active sessions for the employer account and its active team members.'
              : 'This restores access for an employer account that is currently suspended.'}
          </p>
        </div>
        <div className="p-6 sm:p-8">
          <p className="mb-4 text-sm font-semibold text-slate-900 flex items-center gap-2">
            Target: <span className="bg-slate-100 px-2 py-0.5 rounded-md">{action.employer?.companyName}</span>
          </p>
          <label className="block text-sm font-semibold text-slate-700">
            Reason{suspending ? '' : ' (optional)'}
          </label>
          <textarea
            className={`${textareaClass} mt-2`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={suspending ? 'Record the reason for suspension.' : 'Record why access is being restored.'}
          />
          {suspending && reason.trim().length > 0 && reason.trim().length < 10 && (
            <p className="mt-2 text-xs font-semibold text-rose-500">Use at least 10 characters.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 p-6">
          <button type="button" onClick={onClose} disabled={saving} className="h-11 rounded-xl bg-white border border-slate-200 px-6 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 shadow-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(reason)}
            disabled={saving || (suspending && reason.trim().length < 10)}
            className={`inline-flex h-11 items-center gap-2 rounded-xl px-6 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 shadow-sm ${suspending ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {suspending ? 'Suspend' : 'Reactivate'}
          </button>
        </div>
      </section>
    </div>
  );
}

function EmployersListPage() {
  const [employers, setEmployers] = useState([]);
  const [stats, setStats] = useState({ accountStatuses: [], verificationStatuses: [] });
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0, hasMore: false });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    accountStatus: 'all',
    verificationStatus: 'all',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [accountAction, setAccountAction] = useState(null);
  const [savingAction, setSavingAction] = useState(false);

  const loadEmployers = useCallback(async ({ offset = 0, quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const response = await adminAPI.getEmployers({
        ...filters,
        limit: pagination.limit,
        offset
      });
      setEmployers(response.employers || []);
      setStats(response.stats || { accountStatuses: [], verificationStatuses: [] });
      setPagination(response.pagination || { total: 0, limit: pagination.limit, offset, hasMore: false });
    } catch (loadError) {
      setError(extractError(loadError, 'Employers could not be loaded.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    loadEmployers({ offset: 0 });
  }, [loadEmployers]);

  const totals = useMemo(() => {
    const active = stats.accountStatuses?.find((item) => item.status === 'active')?.count || 0;
    const suspended = stats.accountStatuses?.find((item) => item.status === 'suspended')?.count || 0;
    const verified = stats.verificationStatuses?.find((item) => item.status === 'verified')?.count || 0;
    const kycQueue = stats.verificationStatuses
      ?.filter((item) => ['pending', 'in_review', 'requires_resubmission'].includes(item.status))
      .reduce((sum, item) => sum + Number(item.count || 0), 0) || 0;

    return {
      total: stats.total || pagination.total,
      active,
      suspended,
      verified,
      kycQueue
    };
  }, [pagination.total, stats]);

  const resetFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      accountStatus: 'all',
      verificationStatus: 'all',
      sortBy: 'created_at',
      sortOrder: 'DESC'
    });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const nextSearch = searchInput.trim();
    if (nextSearch === filters.search) {
      loadEmployers({ offset: 0 });
      return;
    }
    setFilters((current) => ({ ...current, search: nextSearch }));
  };

  const updateAccountStatus = async (reason) => {
    if (!accountAction) return;
    setSavingAction(true);

    try {
      await adminAPI.updateEmployerAccountStatus(accountAction.employer.id, {
        status: accountAction.status,
        reason: reason.trim()
      });
      toast.success(accountAction.status === 'suspended' ? 'Employer suspended.' : 'Employer reactivated.');
      setAccountAction(null);
      setSelectedEmployer(null);
      await loadEmployers({ offset: pagination.offset, quiet: true });
    } catch (actionError) {
      toast.error(extractError(actionError, 'Account status could not be updated.'));
    } finally {
      setSavingAction(false);
    }
  };

  if (loading) return <EmployersSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 sm:p-6 lg:p-8 font-sans">
      <div className="mx-auto max-w-[1400px] space-y-6">
        
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Employers</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Manage company profiles, review KYC documents, and monitor active hiring across the platform.
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-[16px] border border-rose-200 bg-white shadow-sm p-4 text-sm text-rose-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                <AlertCircle className="h-5 w-5" />
              </div>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={Building2} label="Total Employers" value={formatNumber(totals.total)} detail="Registered accounts" />
          <StatCard icon={CheckCircle2} label="Active Accounts" value={formatNumber(totals.active)} detail={`${formatNumber(totals.suspended)} suspended`} />
          <StatCard icon={ShieldCheck} label="Verified KYC" value={formatNumber(totals.verified)} detail={`${formatNumber(totals.kycQueue)} need review`} />
          <StatCard icon={Briefcase} label="Rows Filtered" value={formatNumber(employers.length)} detail={`${formatNumber(pagination.total)} matching results`} />
        </div>

        {/* Main List Section */}
        <section className="rounded-[24px] border border-slate-100 bg-white shadow-sm overflow-hidden flex flex-col">
          
          {/* Filters Bar */}
          <div className="border-b border-slate-100 p-5 sm:p-6 bg-white">
            <form onSubmit={submitSearch} className="flex flex-col xl:flex-row gap-4">
              <label className="relative block flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  className={`${inputClass} w-full pl-11`}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search company, email, industry, location..."
                />
              </label>
              <div className="flex flex-col sm:flex-row gap-4 shrink-0">
                <select className={`${inputClass} min-w-[160px] cursor-pointer`} value={filters.accountStatus} onChange={(event) => setFilters((current) => ({ ...current, accountStatus: event.target.value }))}>
                  {accountOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select className={`${inputClass} min-w-[160px] cursor-pointer`} value={filters.verificationStatus} onChange={(event) => setFilters((current) => ({ ...current, verificationStatus: event.target.value }))}>
                  {verificationOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select className={`${inputClass} min-w-[160px] cursor-pointer`} value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))}>
                  {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <button type="submit" className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-6 text-sm font-semibold text-white transition hover:bg-blue-600 shadow-sm shrink-0">
                  Search
                </button>
              </div>
            </form>
          </div>

          {!employers.length ? (
            <EmptyState onReset={resetFilters} />
          ) : (
            <>
              <div className="hidden xl:block overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse min-w-[1200px]">
                  <thead>
                    <tr className="bg-slate-50/50">
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Company</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Contact</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Account access</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">KYC</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Hiring</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Team & Docs</th>
                      <th className="py-4 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Activity</th>
                      <th className="py-4 px-6 text-right text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {employers.map((employer) => (
                      <tr key={employer.id} className="align-top hover:bg-slate-50/80 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4 min-w-[240px]">
                            <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 font-bold overflow-hidden shrink-0 border border-blue-100/50 shadow-sm">
                              {employer.companyLogoUrl ? <img src={employer.companyLogoUrl} alt="" className="w-full h-full object-cover" /> : initialsFor(employer.companyName)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate">{employer.companyName}</p>
                              <p className="text-xs text-slate-500 mt-1 truncate">{employer.industry || employer.sector || 'Industry not set'}</p>
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {employer.companyType && <span className="bg-slate-100 px-2 py-0.5 rounded-md text-[10px] font-medium text-slate-600">{labelize(employer.companyType)}</span>}
                                {(employer.companySize || employer.employeeCountRange) && <span className="bg-slate-100 px-2 py-0.5 rounded-md text-[10px] font-medium text-slate-600">{employer.companySize || employer.employeeCountRange}</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-800 text-sm">{employer.contact?.name || 'Contact not set'}</p>
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {employer.contact?.email || 'No email'}</p>
                          <p className="text-xs text-slate-500 mt-1">{employer.contact?.phone || 'No phone'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <Badge status={employer.accountStatus} label={accountStatusLabel(employer.accountStatus)} />
                          <p className="text-[11px] font-medium text-slate-400 mt-2 capitalize">{labelize(employer.subscriptionPlan)} plan</p>
                        </td>
                        <td className="px-6 py-4">
                          <Badge status={employer.verificationStatus} />
                          <p className="text-[11px] font-medium text-slate-400 mt-2">{formatNumber(employer.stats?.verifiedKycDocuments)} verified docs</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-bold text-slate-900">{formatNumber(employer.stats?.activeJobs)} <span className="text-xs font-semibold text-slate-400">/ {formatNumber(employer.stats?.totalJobs)} jobs</span></p>
                          <p className="text-[11px] text-slate-500 mt-1">{formatNumber(employer.stats?.totalMatches)} matches, {formatNumber(employer.stats?.interviews)} interviews</p>
                          <p className="text-[11px] text-slate-500 mt-1">{formatNumber(employer.stats?.offers)} offers, {formatNumber(employer.stats?.assessments)} assessments</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-[11px] font-medium text-slate-500">{formatNumber(employer.stats?.kycDocuments)} uploaded docs</p>
                          <p className="text-[11px] font-medium text-rose-500 mt-1">{formatNumber(employer.stats?.rejectedKycDocuments)} rejected docs</p>
                          <p className="text-[11px] font-medium text-slate-500 mt-1">{formatNumber(employer.stats?.teamMembers)} team members</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-slate-700">{formatDate(employer.createdAt)}</p>
                          <p className="text-[11px] text-slate-400 mt-1">Login: {formatDate(employer.contact?.lastLoginAt, 'No login')}</p>
                          <p className="text-[11px] text-slate-400 mt-1">Activity: {formatDate(employer.latest?.activityAt, 'No activity')}</p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setSelectedEmployer(employer)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Profile">
                              <Eye className="w-4 h-4" />
                            </button>
                            {employer.accountStatus === 'suspended' ? (
                              <button onClick={() => setAccountAction({ employer, status: 'active' })} className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors shadow-sm border border-emerald-100/50" title="Reactivate Account">
                                <PlayCircle className="w-4 h-4" />
                              </button>
                            ) : (
                              <button onClick={() => setAccountAction({ employer, status: 'suspended' })} className="w-9 h-9 flex items-center justify-center rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors shadow-sm border border-rose-100/50" title="Suspend Account">
                                <PauseCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile layout */}
              <div className="grid gap-4 p-4 xl:hidden">
                {employers.map((employer) => (
                  <article key={employer.id} className="rounded-[20px] border border-slate-100 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-blue-50 text-blue-600 font-bold border border-blue-100/50">
                          {employer.companyLogoUrl ? <img src={employer.companyLogoUrl} alt="" className="h-full w-full object-cover" /> : initialsFor(employer.companyName)}
                        </div>
                        <div className="min-w-0">
                          <h2 className="truncate font-bold text-slate-900 text-lg">{employer.companyName}</h2>
                          <p className="mt-0.5 text-sm text-slate-500">{employer.industry || employer.sector || 'Industry not set'}</p>
                        </div>
                      </div>
                      <Badge status={employer.accountStatus} label={accountStatusLabel(employer.accountStatus)} />
                    </div>

                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      <p className="flex items-center gap-2 text-sm font-medium text-slate-600"><Mail className="h-4 w-4 text-slate-400" /> {employer.contact?.email || 'No email'}</p>
                      <p className="flex items-center gap-2 text-sm font-medium text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {employer.headquartersLocation || 'Location not set'}</p>
                      <p className="flex items-center gap-2 text-sm font-medium text-slate-600"><Globe2 className="h-4 w-4 text-slate-400" /> {employer.websiteUrl || 'Website not set'}</p>
                      <p className="flex items-center gap-2 text-sm font-medium text-slate-600"><UsersRound className="h-4 w-4 text-slate-400" /> {formatNumber(employer.stats?.teamMembers)} team members</p>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                      <div className="rounded-2xl bg-slate-50 p-3 border border-slate-100/50">
                        <p className="text-xl font-bold text-slate-900">{formatNumber(employer.stats?.activeJobs)}</p>
                        <p className="text-xs font-semibold text-slate-400 mt-1 uppercase tracking-wider">Active jobs</p>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-3 border border-slate-100/50">
                        <p className="text-xl font-bold text-slate-900">{formatNumber(employer.stats?.totalMatches)}</p>
                        <p className="text-xs font-semibold text-slate-400 mt-1 uppercase tracking-wider">Matches</p>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-3 border border-slate-100/50">
                        <p className="text-xl font-bold text-slate-900">{formatNumber(employer.stats?.offers)}</p>
                        <p className="text-xs font-semibold text-slate-400 mt-1 uppercase tracking-wider">Offers</p>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-slate-100">
                      <Badge status={employer.verificationStatus} />
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button type="button" onClick={() => setSelectedEmployer(employer)} className="inline-flex h-10 flex-1 sm:flex-none items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
                          <Eye className="h-4 w-4" />
                          View
                        </button>
                        {employer.accountStatus === 'suspended' ? (
                          <button type="button" onClick={() => setAccountAction({ employer, status: 'active' })} className="inline-flex h-10 flex-1 sm:flex-none items-center justify-center gap-2 rounded-xl border border-emerald-100/50 bg-emerald-50 px-4 text-sm font-semibold text-emerald-600 shadow-sm transition hover:bg-emerald-100">
                            <PlayCircle className="h-4 w-4" />
                            Reactivate
                          </button>
                        ) : (
                          <button type="button" onClick={() => setAccountAction({ employer, status: 'suspended' })} className="inline-flex h-10 flex-1 sm:flex-none items-center justify-center gap-2 rounded-xl border border-rose-100/50 bg-rose-50 px-4 text-sm font-semibold text-rose-600 shadow-sm transition hover:bg-rose-100">
                            <PauseCircle className="h-4 w-4" />
                            Suspend
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="flex flex-col gap-4 border-t border-slate-100 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between rounded-b-[24px]">
                <p className="text-sm font-medium text-slate-500">
                  Showing <span className="font-semibold text-slate-900">{formatNumber(pagination.offset + 1)}</span> - <span className="font-semibold text-slate-900">{formatNumber(pagination.offset + employers.length)}</span> of <span className="font-semibold text-slate-900">{formatNumber(pagination.total)}</span>
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.offset <= 0 || refreshing}
                    onClick={() => loadEmployers({ offset: Math.max(0, pagination.offset - pagination.limit), quiet: true })}
                    className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={!pagination.hasMore || refreshing}
                    onClick={() => loadEmployers({ offset: pagination.offset + pagination.limit, quiet: true })}
                    className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <EmployerDetailsModal
        employer={selectedEmployer}
        onClose={() => setSelectedEmployer(null)}
        onAction={(employer, status) => setAccountAction({ employer, status })}
      />
      <AccountActionModal
        action={accountAction}
        onClose={() => setAccountAction(null)}
        onSubmit={updateAccountStatus}
        saving={savingAction}
      />
    </div>
  );
}

export default EmployersListPage;
